import React from "react";
import { useLanguage } from "./LanguageContext";
import { usePageMeta } from "../hooks/usePageMeta";

const H2 = { fontFamily: "'new-science','DM Sans',sans-serif", fontSize: 21, fontWeight: 700, color: "#0f172a", margin: "40px 0 14px", letterSpacing: "-0.01em" };
const H3 = { fontFamily: "'new-science','DM Sans',sans-serif", fontSize: 16, fontWeight: 700, color: "#0f172a", margin: "28px 0 10px" };
const H4 = { fontSize: 14, fontWeight: 700, color: "#0f172a", margin: "20px 0 8px" };
const P = { fontSize: 14.5, color: "#334155", lineHeight: 1.7, margin: "0 0 14px" };
const LI = { fontSize: 14.5, color: "#334155", lineHeight: 1.7, marginBottom: 8 };
const UL = { margin: "0 0 14px", paddingLeft: 22 };

// Parses **bold** spans within a single line of text.
function parseInline(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={i}>{part.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{part}</React.Fragment>
  );
}

// Minimal markdown renderer for the legal documents: #### / ### / ## headings,
// "- " bullet lists, and plain paragraphs (consecutive lines join with <br/>).
function renderMarkdown(md) {
  const lines = (md || "").split("\n");
  const out = [];
  let list = [];
  let para = [];
  let k = 0;

  const flushPara = () => {
    if (para.length) {
      out.push(
        <p key={`p${k++}`} style={P}>
          {para.map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {parseInline(line)}
            </React.Fragment>
          ))}
        </p>
      );
      para = [];
    }
  };
  const flushList = () => {
    if (list.length) {
      out.push(
        <ul key={`ul${k++}`} style={UL}>
          {list.map((item, i) => <li key={i} style={LI}>{parseInline(item)}</li>)}
        </ul>
      );
      list = [];
    }
  };

  lines.forEach((raw) => {
    const line = raw.trim();
    if (!line) { flushPara(); flushList(); return; }
    if (line.startsWith("#### ")) { flushPara(); flushList(); out.push(<h4 key={`h${k++}`} style={H4}>{parseInline(line.slice(5))}</h4>); }
    else if (line.startsWith("### ")) { flushPara(); flushList(); out.push(<h3 key={`h${k++}`} style={H3}>{parseInline(line.slice(4))}</h3>); }
    else if (line.startsWith("## ")) { flushPara(); flushList(); out.push(<h2 key={`h${k++}`} style={H2}>{parseInline(line.slice(3))}</h2>); }
    else if (line.startsWith("- ")) { flushPara(); list.push(line.slice(2)); }
    else { flushList(); para.push(line); }
  });
  flushPara();
  flushList();
  return out;
}

export default function LegalPage({ titleKey, contentKey }) {
  const { t, lang } = useLanguage();
  usePageMeta({ title: `${t(titleKey)} — GeoRev.ai`, robots: "noindex" });
  return (
    <main style={{ fontFamily: "'DM Sans', sans-serif", background: "#f8fafc", flexGrow: 1 }}>
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "64px 40px 88px" }}>
        <h1 style={{ fontFamily: "'new-science','DM Sans',sans-serif", fontSize: "clamp(28px,4vw,40px)", color: "#0f172a", margin: "0 0 10px", letterSpacing: "-0.02em" }}>
          {t(titleKey)}
        </h1>
        <p style={{ fontSize: 13, color: "#94a3b8", margin: "0 0 28px" }}>{t("legal.metaLine")}</p>
        {lang !== "de" && (
          <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.6, background: "#eff6ff", border: "1px solid #dbeafe", borderRadius: 8, padding: "12px 16px", margin: "0 0 32px" }}>
            {t("legal.authoritativeNote")}
          </div>
        )}
        <div>{renderMarkdown(t(contentKey))}</div>
      </div>
    </main>
  );
}
